import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export interface LastSwingData {
  id: string;
  cf_playback_url: string | null;
  cf_stream_uid: string | null;
  created_at: string;
  status: string;
  ai_result: any;
  original_filename: string | null;
}

export function useLastSwing() {
  const { user } = useAuth();
  const [data, setData] = useState<LastSwingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchLastSwing() {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Use type assertion to bypass TypeScript type checking for swing_runs table
        const { data: runData, error: fetchError } = await (supabase
          .from("swing_runs" as any)
          .select("id, cf_playback_url, cf_stream_uid, created_at, status, ai_result, original_filename")
          .eq("user_id", user.id)
          .eq("status", "ready")
          .order("created_at", { ascending: false })
          .limit(1)
          .single()) as any;

        if (fetchError) {
          // No runs found is not an error
          if (fetchError.code === "PGRST116") {
            setData(null);
            return;
          }
          throw fetchError;
        }

        setData(runData as LastSwingData);
      } catch (err) {
        console.error("Error fetching last swing:", err);
        setError(err instanceof Error ? err : new Error("Failed to fetch last swing"));
        setData(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchLastSwing();
  }, [user?.id]);

  return { data, isLoading, error };
}

